"use client";

import React, { Fragment, MouseEvent, useCallback } from "react";
import styled from "styled-components";
import Flex, { FlexProps } from "../Flex";
import Checkbox from "../Checkbox";
import ContextMenu from "../ContextMenu";
import { VirtualItem } from "@tanstack/react-virtual";
import { TableColumn } from "./TableHeader";
import { getColor } from "../utils";

const TableRowWrapper = styled(Flex).attrs<TableRowWrapperProps>(props => {
  return {
    height: props.height,
    gap: "16px",
    alignItems: props.alignItems || "center",
    variations: {
      "&:hover": {
        background: "grayLighter",
      },
    },
  };
})<TableRowWrapperProps>`
  padding: ${props => props.padding || "0px"};
  .asset_row_hover {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }
  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    user-select: text;
    cursor: ${props => (props.disabled ? "not-allowed" : "text")};
  }
  &:hover {
    .asset_row_hover {
      opacity: ${props => (props.disabled ? 0 : 1)};
    }
    cursor: ${({ disabled, onMouseDown }) =>
      disabled ? "not-allowed" : onMouseDown ? "pointer" : "default"};
  }
`;

export const TableCellWrapper = styled(Flex).attrs<TableCellWrapperProps>({
  display: "inline-flex",
  overflow: "hidden",
})<TableCellWrapperProps>`
  user-select: none;
  ${props => props.disabled && `pointer-events: none; opacity: 0.5;`}
  ${TableRowWrapper}:hover & {
    background-color: ${getColor("grayLighter")};
  }
  ${props =>
    props.innerShadow &&
    `
  box-shadow: 3px 0px 24px 16px rgba(0,0,0,0.075);
  clip-path: inset(0px -100px 0px 0px);
  `}
`;

type TableRowWrapperProps = {
  onClick?: (data) => void;
  padding?: string;
  height: string;
  disabled?: boolean;
} & FlexProps;

type TableCellWrapperProps = {
  innerShadow?: boolean;
  disabled?: boolean;
};

type TableRowProps<DataType> = {
  row: VirtualItem;
  columns: TableColumn[];
  dynamicHeight?: boolean;
  data: DataType[];
  renderPagingIndicator?: (virtualRow: VirtualItem) => React.ReactNode;
  onRowClick?: (data, event: MouseEvent) => void;
  checkboxProps?: {
    disabled?: (data: DataType) => boolean;
    selectable?: (data: DataType) => boolean;
    isSelected: (data: DataType) => boolean;
    onClick: (data: DataType) => void;
    CheckboxCell?: React.ComponentType<{ data: DataType; rowIndex: number }>;
  };
  hideFinalBottomBorder?: boolean;
  getHeight: () => number;
  renderContextMenuItems?: (data: DataType) => React.ReactNode;
};

const TableRow = <DataType,>({
  row,
  columns,
  dynamicHeight,
  data,
  hideFinalBottomBorder,
  getHeight,
  onRowClick,
  checkboxProps,
  renderContextMenuItems,
  ...styles
}: TableRowProps<DataType>) => {
  const totalWidth = columns.reduce((acc, column) => acc + column.minWidth, 0);
  const rowData = data[row.index];
  const isDisabled = checkboxProps?.disabled?.(rowData) === true;
  const isNotSelectable = checkboxProps?.selectable?.(rowData) === false;
  const shouldHandleClick = onRowClick && !isDisabled;
  const ref = React.useRef(null);
  const hasContextMenu = renderContextMenuItems && renderContextMenuItems(rowData) !== null;

  const onClickTableRow = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).tagName === "P") return;
      onRowClick(rowData, e);
    },
    [onRowClick, rowData]
  );

  const onOpenContextMenu = useCallback(
    (e: MouseEvent) => {
      if (checkboxProps && !checkboxProps.isSelected(rowData)) {
        onRowClick(rowData, e);
      }
      return true;
    },
    [checkboxProps, onRowClick, rowData]
  );

  const onClickHandler = useCallback(() => {
    checkboxProps?.onClick(rowData);
  }, [checkboxProps, rowData]);

  return (
    <Fragment key={row.key}>
      {hasContextMenu && (
        <ContextMenu onOpenMenu={onOpenContextMenu} id={row.index.toString()} parentRef={ref}>
          {renderContextMenuItems(rowData)}
        </ContextMenu>
      )}
      <TableRowWrapper
        data-index={row.index}
        ref={ref}
        minWidth={`${totalWidth}px`}
        height={!dynamicHeight && `${getHeight()}px`}
        borderBottom={row.index === data.length - 1 && hideFinalBottomBorder ? "none" : "default"}
        onClick={shouldHandleClick ? onClickTableRow : undefined}
        disabled={isDisabled}
        {...styles}>
        {checkboxProps && (
          <TableCellWrapper
            borderRight="default"
            position="sticky"
            left={0}
            zIndex={10}
            justifyContent="center"
            overflow="visible"
            backgroundColor="white"
            height="100%"
            px="16px"
            onClick={checkboxProps.CheckboxCell ? undefined : onClickHandler}>
            {checkboxProps.CheckboxCell ? (
              <checkboxProps.CheckboxCell data={rowData} rowIndex={row.index} />
            ) : (
              <Checkbox
                disabled={isDisabled || isNotSelectable}
                isSelected={checkboxProps.isSelected(rowData)}
                onClick={onClickHandler}
              />
            )}
          </TableCellWrapper>
        )}
        {columns.map((k, kIdx) => (
          <TableCellWrapper
            data-testid={`table-row-${kIdx}`}
            flex={`${k.flexGrow || 1} ${k.flexShrink || 1} ${k.minWidth}px`}
            justifyContent={k.justify || undefined}
            disabled={isDisabled}
            key={kIdx}>
            {k.renderer(rowData)}
          </TableCellWrapper>
        ))}
      </TableRowWrapper>
    </Fragment>
  );
};

export default TableRow;
